import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { mondayOf, type ProjectPortalMember, type ProjectPortalTask } from '@catalyst-psa/shared';

import {
  useAddPortalTimeEntry,
  useClosePortalProject,
  useProjectPortalAssignments,
  useProjectPortalView,
  useProjectPortalWeekStatus,
  useUpdatePortalEndDate,
  useUpdatePortalTaskAssignee,
} from './api.js';

/** Prefers the server's message (e.g. the maintenance 503) over a generic fallback. */
function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Adds `days` to an ISO yyyy-mm-dd date (UTC-safe). */
function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "Jun 7" from an ISO date (UTC-safe). */
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${d ?? 1}`;
}

/** "Jun 7 – Jun 13, 2026" from a week-start ISO date. */
function weekRangeLabel(weekStart: string): string {
  const end = addDaysIso(weekStart, 6);
  const [y] = end.split('-').map(Number);
  return `${shortDate(weekStart)} – ${shortDate(end)}, ${y}`;
}

/** "Jun 7, 2026" from an ISO date (UTC-safe). */
function fullDate(iso: string): string {
  const [y] = iso.split('-').map(Number);
  return `${shortDate(iso)}, ${y}`;
}

/** Context for the add-hours popup: which task/member/day was clicked. */
interface CellContext {
  task: ProjectPortalTask;
  member: ProjectPortalMember;
  dayIso: string;
}

/**
 * A barely-visible clock toggle — the timesheet stays hidden until it is pressed. Kept
 * small and very close to the page background so it reads as an easter-egg affordance.
 */
function ClockToggle({ open, onClick }: { open: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? 'Hide hour entry' : 'Show hour entry'}
      title={open ? 'Hide hour entry' : ''}
      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-200 transition hover:text-slate-400"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        className="h-3.5 w-3.5"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function StatCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

export function PublicProjectPortalPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = useProjectPortalView(token);
  const addEntry = useAddPortalTimeEntry(token);
  const closeProject = useClosePortalProject(token);
  const updateEndDate = useUpdatePortalEndDate(token);

  const [showTimesheet, setShowTimesheet] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [endDateDraft, setEndDateDraft] = useState<string>('');
  const [weekStart, setWeekStart] = useState<string>(() =>
    mondayOf(new Date().toISOString().slice(0, 10)),
  );
  const [activeCell, setActiveCell] = useState<CellContext | null>(null);
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  const weekStatusQuery = useProjectPortalWeekStatus(token, weekStart, showTimesheet);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i)),
    [weekStart],
  );

  const view = viewQuery.data;

  // Seed the editable end-date draft from the loaded project.
  useEffect(() => {
    setEndDateDraft(view?.endDate ?? '');
  }, [view?.endDate]);

  /** Members whose timesheet for the displayed week is locked (approved/submitted). */
  const lockedMembers = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of weekStatusQuery.data?.members ?? []) {
      if (!m.editable) map.set(m.memberId, m.status ?? 'locked');
    }
    return map;
  }, [weekStatusQuery.data]);

  /** Existing hours already logged this week, keyed by `${ticketId}|${memberId}|${dayIso}`. */
  const existingHours = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of weekStatusQuery.data?.entries ?? []) {
      const hours = Number(e.hours);
      if (!Number.isFinite(hours) || hours === 0) continue;
      map.set(`${e.ticketId}|${e.memberId}|${e.workDate}`, hours);
    }
    return map;
  }, [weekStatusQuery.data]);

  // Group tasks by phase for display (preserving the phase order from the view).
  const grouped = useMemo(() => {
    if (!view) return [];
    const order: { phaseId: string | null; phaseName: string; tasks: ProjectPortalTask[] }[] = [];
    const index = new Map<string | null, number>();
    for (const task of view.tasks) {
      const key = task.phaseId;
      let at = index.get(key);
      if (at === undefined) {
        at = order.length;
        index.set(key, at);
        order.push({ phaseId: key, phaseName: task.phaseName ?? 'No phase', tasks: [] });
      }
      order[at]!.tasks.push(task);
    }
    return order;
  }, [view]);

  const handleAdd = async (hours: string, note: string): Promise<void> => {
    if (!activeCell) return;
    const memberName = activeCell.member.displayName;
    await addEntry.mutateAsync({
      ticketId: activeCell.task.ticketId,
      memberId: activeCell.member.id,
      workDate: activeCell.dayIso,
      hours,
      notes: note,
    });
    setActiveCell(null);
    setFeedback({ tone: 'ok', message: `Added ${hours} h for ${memberName}.` });
    await Promise.all([viewQuery.refetch(), weekStatusQuery.refetch()]);
  };

  if (viewQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">
        Loading project…
      </div>
    );
  }

  if (viewQuery.isError || !view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">
            This project link is not available. The project may no longer be active, or the link may
            have been regenerated. Please request a new link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{view.projectName}</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              #{view.projectNumber} · {view.customerName}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-right text-xs text-slate-500">
              <p>Manager: {view.managerName}</p>
              {view.startDate || view.endDate ? (
                <p className="mt-0.5">
                  {view.startDate ?? '—'} → {view.endDate ?? '—'}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                void viewQuery.refetch();
                if (showTimesheet) void weekStatusQuery.refetch();
              }}
              disabled={viewQuery.isFetching || weekStatusQuery.isFetching}
              aria-label="Refresh project data"
              title="Refresh"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition hover:bg-slate-50 hover:text-slate-600 disabled:opacity-50"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className={`h-4 w-4 ${viewQuery.isFetching || weekStatusQuery.isFetching ? 'animate-spin' : ''}`}
              >
                <path
                  d="M20 11a8 8 0 1 0-.5 4M20 4v5h-5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Budget" value={`${view.stats.budgetHours} h`} />
          <StatCard label="Consumed" value={`${view.stats.consumedHours} h`} />
          <StatCard label="Remaining" value={`${view.stats.remainingHours} h`} />
          <StatCard
            label="% Consumed"
            value={view.stats.percentConsumed === null ? '—' : `${view.stats.percentConsumed}%`}
          />
        </div>

        {/* Phases */}
        {view.phases.length > 0 ? (
          <div className="mt-6">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Phases
            </p>
            <div className="space-y-2">
              {view.phases.map((phase) => {
                const budget = Number(phase.budgetHours);
                const consumed = Number(phase.consumedHours);
                const pct = budget > 0 ? Math.min(100, Math.round((consumed / budget) * 100)) : 0;
                const remaining = (budget - consumed).toFixed(2);
                return (
                  <div
                    key={phase.id}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-slate-800">
                        {phase.sequence}. {phase.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        Budget: {phase.budgetHours} h · Consumed: {phase.consumedHours} h ·
                        Remaining: {remaining} h ·{' '}
                        {budget > 0 ? `${Math.round((consumed / budget) * 100)}%` : '—'}
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Hour entry (hidden until the clock is pressed — only the clock shows when closed) */}
        <div className="mt-10">
          <ClockToggle open={showTimesheet} onClick={() => setShowTimesheet((v) => !v)} />

          {showTimesheet ? (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                For Catalyst use only
              </p>

              {/* Close project (marks the linked onboarding record "Ready to Bill") */}
              {view && view.status === 'active' ? (
                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  {confirmClose ? (
                    <>
                      <span className="text-xs text-slate-600">
                        Close this project and mark it Ready to Bill?
                      </span>
                      <button
                        type="button"
                        disabled={closeProject.isPending}
                        onClick={() =>
                          closeProject.mutate(undefined, {
                            onSuccess: (res) => {
                              setConfirmClose(false);
                              const base = res.onboardingMarkedReadyToBill
                                ? 'Project closed and onboarding marked Ready to Bill.'
                                : 'Project closed.';
                              setFeedback(
                                res.notificationSent
                                  ? { tone: 'ok', message: `${base} The billing team was notified.` }
                                  : {
                                      tone: 'error',
                                      message: `${base} No notification was sent. ${
                                        res.notificationSkippedReason ?? ''
                                      }`.trim(),
                                    },
                              );
                              void viewQuery.refetch();
                            },
                            onError: (err) =>
                              setFeedback({
                                tone: 'error',
                                message: errorMessage(err, 'Could not close the project.'),
                              }),
                          })
                        }
                        className="rounded-md bg-rose-600 px-3 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-60"
                      >
                        {closeProject.isPending ? 'Closing…' : 'Yes, close project'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmClose(false)}
                        className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-white"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmClose(true)}
                      className="rounded-md border border-rose-300 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                    >
                      Close project &amp; mark Ready to Bill
                    </button>
                  )}
                </div>
              ) : null}

              {/* Project dates: start is read-only, end is editable (Catalyst-only). */}
              {view ? (
                <div className="mb-4 flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Start date
                    </p>
                    <p className="mt-1 text-sm text-slate-800">
                      {view.startDate ? fullDate(view.startDate) : '—'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      End date
                    </label>
                    <input
                      type="date"
                      value={endDateDraft}
                      onChange={(e) => setEndDateDraft(e.target.value)}
                      className="mt-1 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={
                      updateEndDate.isPending ||
                      (endDateDraft ? endDateDraft : null) === (view.endDate ?? null)
                    }
                    onClick={() =>
                      updateEndDate.mutate(
                        { endDate: endDateDraft ? endDateDraft : null },
                        {
                          onSuccess: () => {
                            setFeedback({ tone: 'ok', message: 'Project end date updated.' });
                            void viewQuery.refetch();
                          },
                          onError: (err) =>
                            setFeedback({
                              tone: 'error',
                              message: errorMessage(err, 'Could not update the end date.'),
                            }),
                        },
                      )
                    }
                    className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-white disabled:opacity-60"
                  >
                    {updateEndDate.isPending ? 'Saving…' : 'Save end date'}
                  </button>
                </div>
              ) : null}

              {/* Week navigation */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setWeekStart((w) => addDaysIso(w, -7))}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  ‹ Prev week
                </button>
                <span className="px-2 text-sm font-medium text-slate-800">
                  {weekRangeLabel(weekStart)}
                </span>
                <button
                  type="button"
                  onClick={() => setWeekStart((w) => addDaysIso(w, 7))}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  Next week ›
                </button>
                <button
                  type="button"
                  onClick={() => setWeekStart(mondayOf(new Date().toISOString().slice(0, 10)))}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  This week
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3 font-medium">Task / Member</th>
                      {days.map((iso, i) => (
                        <th key={iso} className="px-2 py-2 text-center font-medium">
                          <div>{DAY_LABELS[i]}</div>
                          <div className="font-normal text-slate-400">{shortDate(iso)}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((group) => (
                      <PhaseRows
                        key={group.phaseId ?? 'none'}
                        phaseName={group.phaseName}
                        tasks={group.tasks}
                        days={days}
                        lockedMembers={lockedMembers}
                        existingHours={existingHours}
                        onPick={(task, member, dayIso) => setActiveCell({ task, member, dayIso })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {grouped.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">
                  This project has no tasks to log hours against yet.
                </p>
              ) : null}

              {feedback ? (
                <p
                  className={`mt-4 text-sm ${
                    feedback.tone === 'ok' ? 'text-emerald-600' : 'text-rose-600'
                  }`}
                >
                  {feedback.message}
                </p>
              ) : null}

              {/* Task assignment editor — revealed by its own small, faint toggle */}
              <div className="mt-6 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAssignments((v) => !v)}
                  aria-label={showAssignments ? 'Hide task assignments' : 'Show task assignments'}
                  title={showAssignments ? 'Hide task assignments' : ''}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-200 transition hover:text-slate-400"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="h-3.5 w-3.5"
                  >
                    <path
                      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM19 8v6M22 11h-6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {showAssignments ? <AssignmentsPanel token={token} /> : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {activeCell ? (
        <AddHoursModal
          cell={activeCell}
          dayLabel={shortDate(activeCell.dayIso)}
          onClose={() => setActiveCell(null)}
          onSubmit={handleAdd}
        />
      ) : null}
    </div>
  );
}

/**
 * Panel that lets an operator add or remove individual members on each project task.
 * Assignments are mutated one member at a time (idempotent) and the local view is kept in
 * sync with the server's authoritative response after every toggle.
 */
function AssignmentsPanel({ token }: { token: string }): JSX.Element {
  const query = useProjectPortalAssignments(token, true);
  const update = useUpdatePortalTaskAssignee(token);

  const [assignedByTask, setAssignedByTask] = useState<Map<string, Set<string>>>(new Map());
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed local assignment state from the server view whenever it (re)loads.
  useEffect(() => {
    if (!query.data) return;
    const map = new Map<string, Set<string>>();
    for (const task of query.data.tasks) {
      map.set(task.ticketId, new Set(task.assigneeMemberIds));
    }
    setAssignedByTask(map);
  }, [query.data]);

  const toggle = async (ticketId: string, memberId: string, assigned: boolean): Promise<void> => {
    setPending(`${ticketId}|${memberId}`);
    setError(null);
    try {
      const updated = await update.mutateAsync({
        ticketId,
        memberId,
        action: assigned ? 'remove' : 'add',
      });
      setAssignedByTask((prev) => {
        const next = new Map(prev);
        next.set(ticketId, new Set(updated.assigneeMemberIds));
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the assignment.');
    } finally {
      setPending(null);
    }
  };

  if (query.isLoading) {
    return <p className="mt-3 text-sm text-slate-500">Loading tasks…</p>;
  }
  if (query.isError || !query.data) {
    return <p className="mt-3 text-sm text-rose-600">Could not load task assignments.</p>;
  }

  const { members, tasks } = query.data;

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="mb-1 text-sm font-semibold text-slate-800">Task assignments</p>
      <p className="mb-4 text-xs text-slate-500">
        Toggle a member to add or remove them from a task. Changes save immediately.
      </p>

      {error ? <p className="mb-3 text-sm text-rose-600">{error}</p> : null}

      {tasks.length === 0 ? (
        <p className="text-sm text-slate-500">This project has no tasks yet.</p>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => {
            const assigned = assignedByTask.get(task.ticketId) ?? new Set<string>();
            return (
              <div key={task.ticketId} className="rounded-lg border border-slate-100 p-3">
                <p className="text-sm font-medium text-slate-800">
                  <span className="font-mono text-xs text-slate-400">#{task.number}</span>{' '}
                  {task.title}
                  {task.phaseName ? (
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      {task.phaseName}
                    </span>
                  ) : null}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {members.map((member) => {
                    const isAssigned = assigned.has(member.id);
                    const isPending = pending === `${task.ticketId}|${member.id}`;
                    return (
                      <button
                        key={member.id}
                        type="button"
                        disabled={isPending}
                        onClick={() => void toggle(task.ticketId, member.id, isAssigned)}
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition disabled:opacity-50 ${
                          isAssigned
                            ? 'border-brand-300 bg-brand-50 text-brand-700 hover:border-brand-400'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="text-sm leading-none">{isAssigned ? '×' : '+'}</span>
                        {member.displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Renders a phase header row followed by one member row per project member for each task. */
function PhaseRows({
  phaseName,
  tasks,
  days,
  lockedMembers,
  existingHours,
  onPick,
}: {
  phaseName: string;
  tasks: ProjectPortalTask[];
  days: string[];
  lockedMembers: Map<string, string>;
  existingHours: Map<string, number>;
  onPick: (task: ProjectPortalTask, member: ProjectPortalMember, dayIso: string) => void;
}): JSX.Element {
  return (
    <>
      <tr className="bg-slate-50">
        <td colSpan={days.length + 1} className="px-1 py-1.5 text-xs font-semibold text-slate-600">
          {phaseName}
        </td>
      </tr>
      {tasks.map((task) => (
        <Fragment key={task.ticketId}>
          <tr className="border-b border-slate-100">
            <td colSpan={days.length + 1} className="px-1 pt-2 text-sm font-medium text-slate-800">
              <span className="font-mono text-xs text-slate-400">#{task.number}</span> {task.title}
              {task.timeEntryLocked ? (
                <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Closed — time locked
                </span>
              ) : null}
              {task.assignees.length === 0 ? (
                <span className="ml-2 text-xs italic text-slate-400">no members assigned</span>
              ) : null}
            </td>
          </tr>
          {task.assignees.map((member) => {
            const lockedStatus = lockedMembers.get(member.id);
            const locked = lockedStatus !== undefined || task.timeEntryLocked;
            return (
              <tr
                key={`${task.ticketId}-${member.id}`}
                className={`border-b border-slate-50 ${locked ? 'opacity-50' : ''}`}
              >
                <td className="py-1.5 pl-4 pr-3 text-slate-600">
                  {member.displayName}
                  {lockedStatus !== undefined ? (
                    <span className="ml-2 text-[11px] uppercase tracking-wide text-slate-400">
                      {lockedStatus}
                    </span>
                  ) : null}
                </td>
                {days.map((iso) => {
                  const existing = existingHours.get(`${task.ticketId}|${member.id}|${iso}`);
                  if (locked) {
                    return (
                      <td key={iso} className="px-1 py-1 text-center">
                        <span className="inline-block h-7 w-12 leading-7 text-slate-400 tabular-nums">
                          {existing ? existing : '—'}
                        </span>
                      </td>
                    );
                  }
                  return (
                    <td key={iso} className="px-1 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => onPick(task, member, iso)}
                        title={existing ? `${existing} h logged — add more` : 'Add hours'}
                        className={`inline-flex h-7 w-12 items-center justify-center rounded border text-sm tabular-nums transition ${
                          existing
                            ? 'border-emerald-200 bg-emerald-50 font-medium text-emerald-700 hover:border-emerald-400'
                            : 'border-slate-200 text-slate-300 hover:border-brand-400 hover:text-brand-500'
                        }`}
                      >
                        {existing ? existing : '+'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </Fragment>
      ))}
    </>
  );
}

/** Popup that collects hours + a required note before adding a single time entry. */
function AddHoursModal({
  cell,
  dayLabel,
  onClose,
  onSubmit,
}: {
  cell: CellContext;
  dayLabel: string;
  onClose: () => void;
  onSubmit: (hours: string, note: string) => Promise<void>;
}): JSX.Element {
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const value = Number(hours);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter a number of hours greater than 0.');
      return;
    }
    if (!note.trim()) {
      setError('A note is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(String(value), note.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add hours.');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">Add hours</h2>
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-mono text-xs text-slate-400">#{cell.task.number}</span>{' '}
          {cell.task.title}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-1.5 text-lg font-bold text-white">
            {cell.member.displayName}
          </span>
          <span className="text-base font-semibold text-slate-700">{dayLabel}</span>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600">Hours</label>
            <input
              type="number"
              min="0"
              step="0.25"
              inputMode="decimal"
              autoFocus
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">
              Note <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What was done?"
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? 'Adding…' : 'Add hours'}
          </button>
        </div>
      </div>
    </div>
  );
}
